import { parseSupportedLanguageOrDefault } from '@pmi.web/localization';
import { ReactNode, useEffect } from 'react';
import {
  type DefaultValues,
  type Path,
  useForm,
  type FieldValues,
  FormProvider,
} from 'react-hook-form';

import { Translatable } from './Translatable';

export function Form<T extends FieldValues>({
  formId,
  language,
  children,
  onSubmit,
  defaultValues,
  businessLogicErrors,
}: {
  readonly formId: string;
  readonly language: string;
  readonly children: ReactNode;
  readonly onSubmit: (data: T) => void;
  readonly defaultValues: DefaultValues<T>;
  readonly businessLogicErrors: Partial<
    Record<Path<T>, { readonly type: string; readonly message: string }>
  >;
}) {
  const methods = useForm<T>({
    defaultValues: defaultValues,
  });

  const internalOnSubmit = (data: T) => {
    deleteEmptyValues(data);
    onSubmit(data);
  };

  useEffect(() => {
    Object.keys(businessLogicErrors).forEach(prop => {
      methods.setError(
        prop as Path<T>,
        businessLogicErrors[prop as Path<T>] as {
          readonly type: string;
          readonly message: string;
        },
      );
    });
  }, [businessLogicErrors, methods]);

  return (
    <Translatable language={parseSupportedLanguageOrDefault(language ?? '')}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(internalOnSubmit)} id={formId}>
          {children}
        </form>
      </FormProvider>
    </Translatable>
  );
}

function deleteEmptyValues(obj: object) {
  Object.keys(obj).forEach(key => deletePropertyfEmpty(obj, key));
}

function deletePropertyfEmpty(obj: object, key: string) {
  const value = (obj as never)[key];

  if (typeof value === 'object' && Object.keys(value).length > 0) {
    deleteEmptyValues(value);
  }

  if (
    value === '' ||
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  ) {
    delete (obj as never)[key];
  }
}
